import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ShiftSlider.module.css';

interface ShiftSliderProps {
  id: string;
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  ariaLabelledBy?: string;
  ariaLabel?: string;
}

const ShiftSlider: React.FC<ShiftSliderProps> = ({
  id,
  min,
  max,
  value,
  onChange,
  ariaLabelledBy,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLInputElement>(null);
  const filledTrackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const updateThumbPosition = useCallback((percent: number) => {
    if (filledTrackRef.current && thumbRef.current) {
      filledTrackRef.current.style.width = `${percent}%`;
      thumbRef.current.style.left = `${percent}%`;
    }
  }, []);

  const handleDrag = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const newValue = Math.round((percent / 100) * (max - min) + min);
    if (newValue !== value) onChange(newValue);
  }, [min, max, value, onChange]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    handleDrag(e.clientX);
  }, [handleDrag]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) handleDrag(e.clientX);
  }, [isDragging, handleDrag]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (isDragging && e.touches[0]) handleDrag(e.touches[0].clientX);
  }, [isDragging, handleDrag]);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  useEffect(() => {
    const percent = ((value - min) / (max - min)) * 100;
    updateThumbPosition(percent);
  }, [value, min, max, updateThumbPosition]);

  return (
    <div className="w-full flex flex-col">
      <div className="flex justify-between mb-3">
        <span className="text-sm text-gray-700">{t('settings.compression.high')}</span>
        <span className="text-sm text-gray-700">{t('settings.compression.quality')}</span>
      </div>

      <div className="relative">
        <div
          ref={containerRef}
          className={styles.track}
          onMouseDown={handleMouseDown}
          onTouchStart={(e) => {
            setIsDragging(true);
            handleDrag(e.touches[0].clientX);
          }}
        >
          <div className={styles.trackBg} />
          <div ref={filledTrackRef} className={styles.trackFill} />
          <div
            ref={thumbRef}
            aria-hidden="true"
            className={`${styles.thumb} ${isDragging ? styles.thumbDragging : ''}`}
          >
            <div className={styles.thumbDot} />
          </div>
        </div>

          <input
            ref={sliderRef}
            id={id}
            type="range"
            min={min}
            max={max}
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            className={styles.nativeInput}
            aria-labelledby={ariaLabelledBy}
            aria-label={ariaLabel}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
          />
        </div>
    </div>
  );
};

export default ShiftSlider;
